import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";

import {
  fetchVarieties,
  createVariety,
  updateVariety,
  deleteVariety,
} from "../api/varietyApi";

import {
  fetchFarms,
  fetchZones,
  fetchGHSEs,
  fetchBays,
} from "../api/ghseApi";

import "../styles/ManageVarieties.css";

const ManageVarieties = () => {
  // stems_per_net stored as number (not string) to match backend Integer field
  const [varieties, setVarieties] = useState([]);
  const [form, setForm] = useState({ name: "", stems_per_net: "" });
  const [editingItem, setEditingItem] = useState(null);

  const [farms, setFarms] = useState([]);
  const [zones, setZones] = useState([]);
  const [ghses, setGHSEs] = useState([]);
  const [bays, setBays] = useState([]);

  const [selectedFarm, setSelectedFarm] = useState("");
  const [selectedZone, setSelectedZone] = useState("");
  const [selectedGHSE, setSelectedGHSE] = useState("");
  const [selectedBays, setSelectedBays] = useState([]);
  const [selectedVerticalSection, setSelectedVerticalSection] = useState("");

  // Each location keeps display names for UI but only IDs are sent to backend
  const [locations, setLocations] = useState([]);

  // =====================
  // LOAD
  // =====================
  useEffect(() => {
    loadVarieties();
    fetchFarms().then(setFarms);
  }, []);

  const loadVarieties = async () => {
    try {
      const data = await fetchVarieties();
      setVarieties(data);
    } catch {
      Swal.fire("Error", "Failed to load varieties", "error");
    }
  };

  // =====================
  // CASCADE
  // =====================
  useEffect(() => {
    if (selectedFarm) {
      fetchZones(selectedFarm).then(setZones);
      setSelectedZone("");
      setSelectedGHSE("");
      setSelectedBays([]);
      setSelectedVerticalSection("");
      setBays([]);
    }
  }, [selectedFarm]);

  useEffect(() => {
    if (selectedZone) {
      fetchGHSEs(selectedFarm, selectedZone).then(setGHSEs);
      setSelectedGHSE("");
      setSelectedBays([]);
      setSelectedVerticalSection("");
    }
  }, [selectedZone]);

  useEffect(() => {
    if (selectedGHSE) {
      fetchBays(selectedGHSE).then(setBays);
      setSelectedBays([]);
      setSelectedVerticalSection("");
    }
  }, [selectedGHSE]);

  // =====================
  // ADD LOCATION
  // =====================
  const addLocation = () => {
    if (!selectedFarm || !selectedZone || !selectedGHSE || !selectedBays.length) {
      return Swal.fire("Incomplete", "Choose the farm, zone, GHSE and at least one Bay / Gemba.", "warning");
    }
    const chosenGHSE = ghses.find((g) => Number(g.id) === Number(selectedGHSE));
    const verticalSection = chosenGHSE?.requires_upper_lower ? selectedVerticalSection : null;
    if (chosenGHSE?.requires_upper_lower && !verticalSection) {
      return Swal.fire("Choose a level", "Select Upper, Lower or Both before adding this location.", "info");
    }

    const farm = farms.find((f) => f.id === Number(selectedFarm));
    const zone = zones.find((z) => z.id === Number(selectedZone));
    const ghse = ghses.find((g) => g.id === Number(selectedGHSE));
    const newLocations = [];
    const duplicates = [];
    selectedBays.forEach((bayId) => {
      const sameBay = locations.filter((location) =>
        Number(location.farm_id) === Number(selectedFarm) &&
        Number(location.zone_id) === Number(selectedZone) &&
        Number(location.ghse_id) === Number(selectedGHSE) &&
        Number(location.bay_id) === Number(bayId)
      );
      const overlaps = sameBay.some((location) => {
        const existingSection = location.vertical_section || (chosenGHSE?.requires_upper_lower ? "both" : null);
        if (!chosenGHSE?.requires_upper_lower) return true;
        return verticalSection === "both" || existingSection === "both" || verticalSection === existingSection;
      });
      if (overlaps) { duplicates.push(bayId); return; }
      const bay = bays.find((item) => Number(item.id) === Number(bayId));
      newLocations.push({
        farm_id: Number(selectedFarm),
        zone_id: Number(selectedZone),
        ghse_id: Number(selectedGHSE),
        bay_id: Number(bayId),
        vertical_section: verticalSection,
        farm_name: farm?.name,
        zone_name: zone?.name,
        ghse_name: ghse?.name,
        bay_name: bay?.name,
      });
    });
    if (!newLocations.length) {
      return Swal.fire("Already assigned", "Every selected Bay / Gemba already has an overlapping level assignment.", "warning");
    }
    setLocations((previous) => [...previous, ...newLocations]);
    setSelectedBays([]);
    if (duplicates.length) {
      const names = duplicates.map((id) => bays.find((bay) => Number(bay.id) === Number(id))?.name || `Bay ${id}`).join(", ");
      Swal.fire("Some locations were already assigned", `Added ${newLocations.length} location${newLocations.length === 1 ? "" : "s"}. Skipped: ${names}.`, "info");
    }
  };

  const removeLocation = (index) => {
    setLocations((prev) => prev.filter((_, i) => i !== index));
  };

  // =====================
  // EDIT
  // =====================
  const handleEdit = (item) => {
    setEditingItem(item);
    setForm({
      name: item.name || "",
      // Coerce to string for the input, will be converted to Number on submit
      stems_per_net: item.stems_per_net != null ? String(item.stems_per_net) : "",
    });
    // locations_detail comes back from the API WITHOUT the write_only ID fields
    // (farm_id, zone_id, ghse_id, bay_id are write_only=True on the serializer,
    //  so they are never included in GET responses).
    // We reconstruct each location object by fetching the ID from the VarietyLocation
    // model's raw id field using the name fields via the already-loaded lookup data,
    // OR — more reliably — we ask the backend to also expose the IDs as readable.
    //
    // Frontend-side fix: the VarietyLocationSerializer DOES store farm_id etc. on the
    // model instance. They just aren't serialised for output. The simplest fix here is
    // to update the serializer (see note below). Until then we reconstruct from the
    // `id` field on each location_detail object by re-fetching, OR we rely on the
    // backend fix below.
    //
    // ✅ Real fix: remove write_only=True from farm_id/zone_id/ghse_id/bay_id in
    // VarietyLocationSerializer so they are returned on read.  Then this just works:
    setLocations(
      (item.locations_detail || []).map((l) => ({
        farm_id: l.farm_id,
        zone_id: l.zone_id,
        ghse_id: l.ghse_id,
        bay_id: l.bay_id,
        vertical_section: l.vertical_section || null,
        farm_name: l.farm_name,
        zone_name: l.zone_name,
        ghse_name: l.ghse_name,
        bay_name: l.bay_name,
      }))
    );
    setSelectedFarm("");
    setSelectedZone("");
    setSelectedGHSE("");
    setSelectedBays([]);
    setSelectedVerticalSection("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // =====================
  // RESET
  // =====================
  const resetForm = () => {
    setForm({ name: "", stems_per_net: "" });
    setEditingItem(null);
    setLocations([]);
    setSelectedFarm("");
    setSelectedZone("");
    setSelectedGHSE("");
    setSelectedBays([]);
    setSelectedVerticalSection("");
  };

  // =====================
  // BUILD PAYLOAD
  // Strips all display-only fields (farm_name, zone_name, etc.)
  // Sends only the integer IDs the backend write-only fields expect.
  // Always sends `locations` key so the backend update() triggers
  // the replace logic (locations_data is not None).
  // =====================
  const buildPayload = () => ({
    name: form.name.trim(),
    stems_per_net: Number(form.stems_per_net),   // backend expects Integer
    locations: locations.map((l) => ({
      farm_id: l.farm_id,
      zone_id: l.zone_id,
      ghse_id: l.ghse_id,
      bay_id: l.bay_id,
      vertical_section: l.vertical_section || null,
    })),
  });

  // =====================
  // SUBMIT
  // =====================
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name.trim())
      return Swal.fire("Required", "Variety name is required", "error");

    const stemsNum = Number(form.stems_per_net);
    if (!form.stems_per_net || isNaN(stemsNum) || stemsNum <= 0)
      return Swal.fire("Required", "Enter a valid stems per net value", "error");

    try {
      const payload = buildPayload();

      if (editingItem) {
        await updateVariety(editingItem.id, payload);
        Swal.fire("Updated!", "Variety updated successfully", "success");
      } else {
        await createVariety(payload);
        Swal.fire("Created!", "Variety created successfully", "success");
      }

      resetForm();
      loadVarieties();
    } catch (err) {
      Swal.fire(
        "Error",
        err.response?.data?.detail ||
          JSON.stringify(err.response?.data) ||
          "Operation failed",
        "error"
      );
    }
  };

  // =====================
  // DELETE
  // Backend destroy() cleans up varietylocation_set before deleting —
  // no extra payload needed, just the id in the URL.
  // =====================
  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "Delete this variety?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#a92f6f",
      cancelButtonColor: "#a8a49a",
      confirmButtonText: "Yes, delete it",
    });

    if (!confirm.isConfirmed) return;

    try {
      await deleteVariety(id);
      loadVarieties();
      Swal.fire("Deleted!", "Variety has been removed", "success");
    } catch {
      Swal.fire("Error", "Delete failed", "error");
    }
  };

  const activeGHSE = ghses.find((g) => Number(g.id) === Number(selectedGHSE));

  // =====================
  // RENDER
  // =====================
  return (
    <div className="mv-root">

      {/* ── Page Header ── */}
      <div className="mv-header">
        <h1 className="mv-header-title">
          Manage <span>Varieties</span>
        </h1>
        <span className="mv-header-badge">
          {varieties.length} {varieties.length === 1 ? "Variety" : "Varieties"}
        </span>
      </div>

      <div className="mv-layout">

        {/* ── Left Panel: Form ── */}
        <div className="mv-card">
          <div className="mv-card-header">
            <div className="mv-card-icon mv-card-icon--rose">🌿</div>
            <div>
              <p className="mv-card-title">
                {editingItem ? "Edit Variety" : "New Variety"}
              </p>
              <p className="mv-card-subtitle">
                {editingItem ? `Editing: ${editingItem.name}` : "Add a new plant variety"}
              </p>
            </div>
          </div>

          <div className="mv-card-body">
            {editingItem && (
              <div className="mv-edit-banner">
                <span className="mv-edit-banner-icon">✏️</span>
                You are editing <strong>&nbsp;{editingItem.name}</strong>
              </div>
            )}

            <form className="mv-form" onSubmit={handleSubmit}>

              {/* Basic Info */}
              <p className="mv-section-label">Basic Info</p>

              <div className="mv-field">
                <label className="mv-label">
                  Variety Name <span className="mv-required">*</span>
                </label>
                <input
                  className="mv-input"
                  placeholder="e.g. Freedom Red"
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  required
                />
              </div>

              <div className="mv-field">
                <label className="mv-label">
                  Stems per Net <span className="mv-required">*</span>
                </label>
                <input
                  className="mv-input"
                  type="number"
                  placeholder="e.g. 25"
                  value={form.stems_per_net}
                  min="1"
                  onChange={(e) => setForm((p) => ({ ...p, stems_per_net: e.target.value }))}
                  required
                />
              </div>

              <div className="mv-divider" />

              {/* Location Hierarchy */}
              <p className="mv-section-label">Assign Location</p>

              <div className="mv-location-grid">
                <div className="mv-field">
                  <label className="mv-label">Farm</label>
                  <select
                    className="mv-select"
                    value={selectedFarm}
                    onChange={(e) => setSelectedFarm(e.target.value)}
                  >
                    <option value="">Select Farm</option>
                    {farms.map((f) => (
                      <option key={f.id} value={f.id}>{f.name}</option>
                    ))}
                  </select>
                </div>

                <div className="mv-field">
                  <label className="mv-label">Zone</label>
                  <select
                    className="mv-select"
                    value={selectedZone}
                    onChange={(e) => setSelectedZone(e.target.value)}
                    disabled={!selectedFarm}
                  >
                    <option value="">Select Zone</option>
                    {zones.map((z) => (
                      <option key={z.id} value={z.id}>{z.name}</option>
                    ))}
                  </select>
                </div>

                <div className="mv-field">
                  <label className="mv-label">GHSE</label>
                  <select
                    className="mv-select"
                    value={selectedGHSE}
                    onChange={(e) => { setSelectedGHSE(e.target.value); setSelectedVerticalSection(""); }}
                    disabled={!selectedZone}
                  >
                    <option value="">Select GHSE</option>
                    {ghses.map((g) => (
                      <option key={g.id} value={g.id}>{g.name}</option>
                    ))}
                  </select>
                </div>

              </div>

              <div className={`mv-bay-picker${!selectedGHSE ? " is-locked" : ""}`}>
                <div className="mv-bay-picker-head">
                  <div><strong>Select Bays / Gembas</strong><small>{selectedBays.length} of {bays.length} selected</small></div>
                  {bays.length > 1 && <button type="button" disabled={!selectedGHSE} onClick={() => setSelectedBays(selectedBays.length === bays.length ? [] : bays.map((bay) => Number(bay.id)))}>{selectedBays.length === bays.length ? "Clear all" : "Select all"}</button>}
                </div>
                {!selectedGHSE ? <p className="mv-bay-picker-empty">Choose a GHSE first to load its Bays / Gembas.</p> : bays.length ? <div className="mv-bay-options">
                  {bays.map((bay) => {
                    const checked = selectedBays.includes(Number(bay.id));
                    return <label key={bay.id} className={checked ? "is-selected" : ""}>
                      <input type="checkbox" checked={checked} onChange={(event) => setSelectedBays((current) => event.target.checked ? [...current, Number(bay.id)] : current.filter((id) => id !== Number(bay.id)))} />
                      <span className="mv-bay-check" aria-hidden="true">{checked ? "✓" : ""}</span>
                      <span className="mv-bay-name">{bay.name}</span>
                      <span className="mv-bay-kind">GEMBA</span>
                    </label>;
                  })}
                </div> : <p className="mv-bay-picker-empty">No Bays / Gembas are configured for this GHSE.</p>}
              </div>

              {activeGHSE?.requires_upper_lower && <fieldset className="mv-level-picker">
                <legend>Where does this variety grow?</legend>
                <p>Apply this level to every selected Bay / Gemba.</p>
                <div className="mv-level-options">
                  {[["upper", "Upper", "Top canopy"], ["lower", "Lower", "Lower canopy"], ["both", "Both", "Available at both levels"]].map(([value, title, detail]) => <label key={value} className={selectedVerticalSection === value ? "is-selected" : ""}><input type="radio" name="variety-vertical-section" value={value} checked={selectedVerticalSection === value} onChange={(e) => setSelectedVerticalSection(e.target.value)} /><span className="mv-level-mark">{value === "both" ? "↕" : value === "upper" ? "U" : "L"}</span><span className="mv-level-copy"><strong>{title}</strong><small>{detail}</small></span></label>)}
                </div>
              </fieldset>}

              <button
                type="button"
                className="mv-btn mv-btn--add"
                onClick={addLocation}
                disabled={!selectedBays.length || (activeGHSE?.requires_upper_lower && !selectedVerticalSection)}
              >
                + Add Location
              </button>

              {/* Location Chips */}
              <div className="mv-locations-section">
                <p className="mv-locations-label">Added Locations</p>
                <div className="mv-location-list">
                  {locations.length === 0 ? (
                    <p className="mv-location-empty">No locations added yet</p>
                  ) : (
                    locations.map((l, i) => (
                      <div className="mv-location-chip" key={i}>
                        <div className="mv-location-chip-path">
                          <span className="mv-location-chip-seg">{l.farm_name}</span>
                          <span className="mv-location-chip-arrow">›</span>
                          <span className="mv-location-chip-seg">{l.zone_name}</span>
                          <span className="mv-location-chip-arrow">›</span>
                          <span className="mv-location-chip-seg">{l.ghse_name}</span>
                          <span className="mv-location-chip-arrow">›</span>
                          <span className="mv-location-chip-seg">{l.bay_name}</span>
                          {l.vertical_section && <span className={`mv-level-tag is-${l.vertical_section}`}>{l.vertical_section === "both" ? "Upper + Lower" : `${l.vertical_section[0].toUpperCase()}${l.vertical_section.slice(1)} level`}</span>}
                        </div>
                        <button
                          type="button"
                          className="mv-location-remove"
                          onClick={() => removeLocation(i)}
                          title="Remove"
                        >
                          ×
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="mv-btn-row">
                <button type="submit" className="mv-btn mv-btn--primary">
                  {editingItem ? "✓ Update Variety" : "+ Create Variety"}
                </button>
                {editingItem && (
                  <button type="button" className="mv-btn mv-btn--ghost" onClick={resetForm}>
                    Cancel
                  </button>
                )}
              </div>

            </form>
          </div>
        </div>

        {/* ── Right Panel: Table ── */}
        <div className="mv-card">
          <div className="mv-card-header">
            <div className="mv-card-icon mv-card-icon--olive">📋</div>
            <div>
              <p className="mv-card-title">All Varieties</p>
              <p className="mv-card-subtitle">Manage existing plant varieties</p>
            </div>
          </div>

          <div className="mv-table-wrapper">
            <table className="mv-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Stems / Net</th>
                  <th>Locations</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {varieties.length === 0 ? (
                  <tr>
                    <td colSpan={4}>
                      <div className="mv-empty-state">
                        <div className="mv-empty-icon">🌱</div>
                        <p className="mv-empty-text">No varieties found</p>
                        <p className="mv-empty-sub">Create your first variety using the form</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  varieties.map((v) => (
                    <tr key={v.id}>
                      <td>
                        <span className="mv-table-name">{v.name}</span>
                      </td>
                      <td>
                        <span className="mv-table-stems">{v.stems_per_net}</span>
                      </td>
                      <td>
                        <div className="mv-table-loc">
                          {v.locations_detail?.length > 0 ? (
                            v.locations_detail.map((l, i) => (
                              <span className="mv-table-loc-item" key={i}>
                                <span>{l.farm_name}</span>
                                <span className="sep">›</span>
                                <span>{l.zone_name}</span>
                                <span className="sep">›</span>
                                <span>{l.ghse_name}</span>
                                <span className="sep">›</span>
                                <span>{l.bay_name}</span>
                                {l.vertical_section && <span className={`mv-level-tag is-${l.vertical_section}`}>{l.vertical_section === "both" ? "Upper + Lower" : `${l.vertical_section[0].toUpperCase()}${l.vertical_section.slice(1)}`}</span>}
                              </span>
                            ))
                          ) : (
                            <span style={{ color: "var(--neutral-400)", fontSize: "12px" }}>—</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="mv-table-actions">
                          <button
                            className="mv-btn mv-btn--edit"
                            onClick={() => handleEdit(v)}
                          >
                            Edit
                          </button>
                          <button
                            className="mv-btn mv-btn--danger-ghost"
                            onClick={() => handleDelete(v.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ManageVarieties;
